import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Eye, Warning, ArrowSquareOut } from '@phosphor-icons/react';
import DOMPurify from 'dompurify';

/**
 * StyledHtmlPreview
 * -----------------
 * Renders a full HTML document (including its own <style> block) in an isolated
 * iframe so the preview matches exactly what the PDF/HTML export will produce.
 *
 * Security model (defense-in-depth):
 *   1. `sandbox="allow-same-origin"` on the iframe — scripts cannot run because
 *      `allow-scripts` is NOT included. Top-level navigation blocked.
 *   2. Every HTML string is passed through DOMPurify before being set as
 *      `srcDoc`. This strips <script> tags, inline event handlers
 *      (onclick/onerror/…), and javascript: URIs, while preserving <style>
 *      blocks + inline `style=` attributes so the visual output is unchanged.
 *
 * DOMPurify is configured to keep the full-document structure so the
 * generated ebook/sales-page CSS still renders exactly as the PDF does.
 */
export default function StyledHtmlPreview({ html, title = 'Live Preview', downloadName = 'preview.html' }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(500);
  const [loadError, setLoadError] = useState(null);

  // Sanitize once per html change. `WHOLE_DOCUMENT: true` preserves
  // <!doctype>/<html>/<head>/<body>; we explicitly keep <style> so typography,
  // gradients and page breaks render identically to the PDF export.
  const sanitizedHtml = useMemo(() => {
    if (!html || typeof html !== 'string') return '';
    try {
      return DOMPurify.sanitize(html, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ['style'],
        FORBID_TAGS: ['script', 'object', 'embed', 'iframe'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onkeydown', 'onkeyup'],
      });
    } catch (err) {
      console.warn('[StyledHtmlPreview] sanitize failed, falling back to empty', err);
      return '';
    }
  }, [html]);

  useEffect(() => {
    if (!html || typeof html !== 'string' || html.length < 50) {
      setLoadError('Preview HTML is empty or too short. Regenerate and try again.');
    } else {
      setLoadError(null);
    }
  }, [html]);

  const handleLoad = () => {
    try {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentDocument) return;
      const doc = iframe.contentDocument;
      // Resize iframe to the content height so there's no inner scroll chrome
      const body = doc.body;
      const htmlEl = doc.documentElement;
      const contentHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        htmlEl?.scrollHeight || 0,
        htmlEl?.offsetHeight || 0,
      );
      if (contentHeight > 0) {
        setHeight(Math.min(contentHeight + 32, 1400));
      }
    } catch (err) {
      console.warn('[StyledHtmlPreview] height resize failed', err);
    }
  };

  const openInNewTab = () => {
    try {
      // Sanitize here too — the new tab renders outside the iframe sandbox.
      const blob = new Blob([sanitizedHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('[StyledHtmlPreview] open-in-new-tab failed', err);
    }
  };

  if (loadError) {
    return (
      <div
        className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start space-x-3"
        data-testid="styled-preview-error"
      >
        <Warning size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-red-300 font-medium text-sm mb-1">Preview unavailable</p>
          <p className="text-red-400/80 text-xs">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4"
      data-testid="styled-html-preview-container"
    >
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-white font-medium flex items-center space-x-2">
          <Eye size={16} className="text-emerald-400" />
          <span>{title}</span>
        </h5>
        <button
          onClick={openInNewTab}
          className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
          data-testid="open-full-preview-btn"
        >
          <ArrowSquareOut size={12} />
          <span>Open Full</span>
        </button>
      </div>
      <div className="bg-white rounded-lg overflow-hidden ring-1 ring-black/5">
        <iframe
          ref={iframeRef}
          title={title}
          srcDoc={sanitizedHtml}
          onLoad={handleLoad}
          sandbox="allow-same-origin"
          className="w-full bg-white"
          style={{ height: `${height}px`, border: 0 }}
          data-testid="styled-html-preview-iframe"
        />
      </div>
      <p className="text-slate-500 text-[11px] mt-2">
        This is exactly what your exported HTML and PDF will look like.
      </p>
    </div>
  );
}
